import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CheckCircle, Calculator, DollarSign, FileText, 
  Calendar, Bell, MapPin, Building, AlertTriangle, Lightbulb,
  PieChart, TrendingUp
} from 'lucide-react';

const TaxSettingsGuide = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'overview',
      title: 'Understanding Tax Settings',
      icon: Calculator,
      content: (
        <>
          <p className="text-gray-700 mb-4">
            SuddyFlow helps you manage two types of taxes that affect your business:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <DollarSign size={18} />
                Sales Tax
              </h4>
              <p className="text-sm text-blue-800">
                Tax you collect from customers and remit to the state. This gets added to your invoices.
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <FileText size={18} />
                Income Tax
              </h4>
              <p className="text-sm text-purple-800">
                Tax you pay on your business profits. SuddyFlow helps estimate quarterly payments and prepare for Schedule C.
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'sales-tax',
      title: 'Setting Up Sales Tax',
      icon: DollarSign,
      steps: [
        "Go to Settings → Tax Settings tab",
        "Toggle 'Collect Sales Tax' to ON",
        "Select your state from the dropdown",
        "Select your county (for states with local taxes like NC)",
        "The combined tax rate will auto-calculate",
        "Click 'Save Tax Settings'"
      ],
      tips: "In North Carolina, sales tax varies by county (6.75% - 7.50%). SuddyFlow looks up the correct rate for your county automatically!",
      content: (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
          <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <AlertTriangle size={18} />
            Do I Need to Collect Sales Tax?
          </h4>
          <p className="text-sm text-amber-800 mb-2">
            It depends on your state and services. In most states, pressure washing and exterior cleaning services ARE taxable. However:
          </p>
          <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
            <li>Some states exempt certain services</li>
            <li>You may need a sales tax permit from your state</li>
            <li>Check with your state's Department of Revenue</li>
          </ul>
        </div>
      )
    },
    {
      id: 'invoices',
      title: 'How Sales Tax Appears on Invoices',
      icon: FileText,
      content: (
        <>
          <p className="text-gray-700 mb-4">
            Once sales tax is enabled, the invoice creation form changes:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-3">With Sales Tax Enabled:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">$350.00</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-gray-600">Tax (6.75%)</span>
                <span className="font-medium">$23.63</span>
              </div>
              <div className="flex justify-between py-1 font-bold">
                <span>Total</span>
                <span>$373.63</span>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Lightbulb size={18} />
              "Quote Includes Tax" Option
            </h4>
            <p className="text-sm text-blue-800">
              If you quoted a customer $400 and that price already includes tax, check the "Quote amount includes tax" box. 
              SuddyFlow will reverse-calculate: Subtotal = $374.53, Tax = $25.47, Total = $400.00
            </p>
          </div>
        </>
      )
    },
    {
      id: 'quarterly',
      title: 'Quarterly Estimated Taxes',
      icon: Calendar,
      content: (
        <>
          <p className="text-gray-700 mb-4">
            As a self-employed business owner, you're required to pay estimated taxes quarterly instead of waiting until April 15th.
          </p>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Quarter</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Income Period</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Due Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2">Q1</td>
                  <td className="px-4 py-2">Jan 1 - Mar 31</td>
                  <td className="px-4 py-2 font-medium text-primary-600">April 15</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">Q2</td>
                  <td className="px-4 py-2">Apr 1 - May 31</td>
                  <td className="px-4 py-2 font-medium text-primary-600">June 15</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">Q3</td>
                  <td className="px-4 py-2">Jun 1 - Aug 31</td>
                  <td className="px-4 py-2 font-medium text-primary-600">September 15</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">Q4</td>
                  <td className="px-4 py-2">Sep 1 - Dec 31</td>
                  <td className="px-4 py-2 font-medium text-primary-600">January 15</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600">
            Go to <strong>Reports → Quarterly Tax</strong> to see your estimated payment for each quarter based on your actual income and expenses.
          </p>
        </>
      )
    },
    {
      id: 'reminders',
      title: 'Tax Reminders',
      icon: Bell,
      steps: [
        "Go to Settings → Tax Settings tab",
        "Enable 'Quarterly Tax Reminders' to get notified before federal estimated tax due dates",
        "Enable 'Sales Tax Reminders' if you collect sales tax",
        "Set how many days before the due date you want to be reminded (default: 14 days)",
        "Select your sales tax filing frequency (Monthly, Quarterly, or Annually)"
      ],
      tips: "Tax reminders appear on your Dashboard so you never miss a deadline!",
      content: null
    },
    {
      id: 'reports',
      title: 'Tax Reports',
      icon: PieChart,
      content: (
        <>
          <p className="text-gray-700 mb-4">
            SuddyFlow generates three tax-related reports to help you stay organized:
          </p>
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">Profit & Loss Report</h4>
              <p className="text-sm text-green-800">
                Shows your gross revenue, expenses by category, and net profit. Perfect for understanding your business performance 
                and includes a "Tax Set-Aside" recommendation showing how much you should save for taxes.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Schedule C Report</h4>
              <p className="text-sm text-blue-800">
                Organizes your income and expenses by IRS Schedule C line numbers. Hand this to your accountant or use it 
                to fill out your own Schedule C at tax time. Shows estimated self-employment tax too!
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">Quarterly Tax Summary</h4>
              <p className="text-sm text-purple-800">
                Breaks down each quarter's income, deductions, and estimated tax payment. Shows self-employment tax (15.3%), 
                federal income tax estimate, and state income tax based on your settings.
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-700 flex items-start gap-2">
              <FileText size={18} className="text-gray-500 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Export to PDF:</strong> All reports can be exported to PDF for your records or to share with your accountant. 
                Just click the "Export PDF" button at the top of any report.
              </span>
            </p>
          </div>
        </>
      )
    },
    {
      id: 'expenses',
      title: 'Tracking Deductible Expenses',
      icon: TrendingUp,
      content: (
        <>
          <p className="text-gray-700 mb-4">
            Every expense you track in SuddyFlow can be marked as tax-deductible. The more expenses you track, the lower your taxable income!
          </p>
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-3">Common Deductible Expenses:</h4>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Fuel for work vehicle</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Chemicals & supplies</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Equipment purchases</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Vehicle maintenance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Business insurance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Advertising & marketing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Software subscriptions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span>Business phone</span>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Lightbulb size={18} />
              Pro Tip: Business Use Percentage
            </h4>
            <p className="text-sm text-blue-800">
              If you use something for both business and personal (like your phone or vehicle), set the "Business Use %" 
              when adding the expense. For example, if your truck is used 90% for business, set it to 90% and SuddyFlow 
              will calculate the correct deductible amount.
            </p>
          </div>
        </>
      )
    }
  ];

  const faqs = [
    {
      question: "Do I really need to pay quarterly taxes?",
      answer: "If you expect to owe $1,000 or more in taxes for the year, the IRS requires quarterly estimated payments. Missing these can result in penalties. SuddyFlow's Quarterly Tax report helps you estimate what to pay each quarter."
    },
    {
      question: "What's self-employment tax?",
      answer: "Self-employment tax is 15.3% of your net profit (12.4% for Social Security + 2.9% for Medicare). As a W-2 employee, your employer pays half. As self-employed, you pay both halves. The good news: you can deduct half of this on your tax return!"
    },
    {
      question: "Should I save receipts if I'm tracking expenses in SuddyFlow?",
      answer: "Yes! Keep receipts for at least 3 years (7 years is safer). SuddyFlow helps you organize and categorize expenses, but you still need proof of purchase if the IRS ever audits you. Consider using SuddySnap to photograph and attach receipts to expenses."
    },
    {
      question: "How accurate are SuddyFlow's tax estimates?",
      answer: "They're rough estimates based on standard tax rates and your income/expenses in SuddyFlow. Your actual tax liability depends on many factors (filing status, other income, deductions, credits). Always consult a tax professional for your specific situation."
    },
    {
      question: "I don't collect sales tax. Should I start?",
      answer: "Check your state's requirements. Most states require sales tax on services like pressure washing. Operating without collecting required sales tax can result in back taxes, penalties, and interest. When in doubt, consult your state's Department of Revenue or a tax professional."
    },
    {
      question: "What's a Schedule C?",
      answer: "Schedule C is an IRS form that sole proprietors use to report business income and expenses. It's attached to your personal tax return (Form 1040). SuddyFlow's Schedule C report organizes your data by the actual line numbers on the form, making tax prep much easier!"
    }
  ];

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Tax Settings & Reports</h1>
          <p className="text-lg text-gray-600">
            Everything you need to know about managing sales tax, estimated taxes, and tax reporting in SuddyFlow.
          </p>
        </div>

        {/* Quick Links */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-8">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Jump To:</h2>
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-3 py-1.5 bg-gray-100 hover:bg-primary-100 text-gray-700 hover:text-primary-700 rounded-full text-sm transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <div 
                key={section.id} 
                id={section.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden scroll-mt-4"
              >
                {/* Section Header */}
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-600 text-white rounded-lg flex items-center justify-center">
                      <Icon size={22} />
                    </div>
                    {section.title}
                  </h2>
                </div>

                {/* Section Content */}
                <div className="p-6">
                  {section.content}
                  
                  {section.steps && (
                    <>
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
                        How to set it up:
                      </h3>
                      <ol className="space-y-2 mb-4">
                        {section.steps.map((step, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="text-gray-700">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </>
                  )}

                  {section.tips && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 font-bold text-sm">💡 Pro Tip:</span>
                        <p className="text-sm text-blue-900">{section.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQs */}
        <div className="mt-12 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index}>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 text-sm">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-bold text-amber-900 mb-2">Important Disclaimer</h3>
              <p className="text-sm text-amber-800">
                SuddyFlow provides tools to help organize your finances and estimate taxes, but we are not tax professionals. 
                Tax laws vary by state and change frequently. The estimates and reports in SuddyFlow are for informational 
                purposes only and should not be considered tax advice. Always consult a qualified tax professional or CPA 
                for advice specific to your situation.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-8 text-center border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Ready to Set Up Your Taxes? 
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Head to Settings to configure your sales tax rate and tax reminders, then check out Reports to see your tax estimates!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/settings')}
              className="btn btn-primary"
            >
              Go to Tax Settings
            </button>
            <button
              onClick={() => navigate('/reports')}
              className="btn btn-secondary"
            >
              View Tax Reports
            </button>
          </div>
        </div>

        {/* Back to Help Center */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxSettingsGuide;